import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import sidekickService, { mapChatHistoryResponse } from './sidekickService';
import { ChatHistoryData, ChatDetailMessage } from '../../types/sidekick';

interface SidekickErrorPayload {
  message: string;
  statusCode: number | null;
}

interface FetchChatHistoryArgs {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  from?: string;
  to?: string;
  filters?: Record<string, string | number | string[]>;
  requestSource?: 'initial' | 'filter' | 'pagination' | 'sorting';
}

interface FetchChatHistoryPayload {
  data: ChatHistoryData[];
  totalCount: number;
  page: number;
  pageSize: number;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
  requestSource: 'initial' | 'filter' | 'pagination' | 'sorting' | null;
}

interface FetchChatSessionDetailsArgs {
  conversationId: string;
  page?: number;
  pageSize?: number;
  requestSource?: 'initial' | 'pagination';
}

interface FetchChatSessionDetailsPayload {
  data: ChatDetailMessage[];
  totalCount: number;
  totalPages: number;
  page: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  conversationId: string;
  conversationSubject?: string;
  requestSource: 'initial' | 'pagination' | null;
}

const getApiErrorPayload = (error: unknown, fallbackMessage: string): SidekickErrorPayload => {
  const candidate = error as {
    message?: string;
    response?: {
      status?: number;
      data?: { message?: string } | string;
    };
  };

  const statusCode = typeof candidate?.response?.status === 'number'
    ? candidate.response.status
    : null;

  const responseData = candidate?.response?.data;
  const responseMessage =
    typeof responseData === 'string'
      ? responseData
      : responseData?.message;

  return {
    message: responseMessage || candidate?.message || fallbackMessage,
    statusCode
  };
};

// Define the state interface
interface SidekickState {
  chatHistory: {
    data: ChatHistoryData[];
    loading: boolean;
    error: string | null;
    errorStatusCode: number | null;
    totalCount: number;
    currentPage: number; // Current page for pagination
    rowsPerPage: number; // Number of rows per page
    sortField: string; // Field to sort by
    sortDirection: 'asc' | 'desc'; // Sort direction
    filterParams: Record<string, string | number | string[]>; // Optional filters for chat history
    from: string; // Date filter from
    to: string; // Date filter to
    lastRequestSource: 'initial' | 'filter' | 'pagination' | 'sorting' | null;
    dateLabelKey: string | null; // Key of the selected predefined date preset (null = custom range)
  };
  chatDetail: {
    data: ChatDetailMessage[];
    loading: boolean;
    error: string | null;
    errorStatusCode: number | null;
    totalCount: number;
    totalPages: number;
    page: number;
    pageSize: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    conversationId: string | null;
    conversationSubject?: string;
    lastRequestSource: 'initial' | 'pagination' | null;
  };
  currentChatHistory: ChatHistoryData | null;
}

// Initial state
const initialState: SidekickState = {
  chatHistory: {
    data: [],
    loading: false,
    error: null,
    errorStatusCode: null,
    totalCount: 0,
    currentPage: 1, // Start at first page
    rowsPerPage: 10, // Default number of rows per page
    sortField: 'conversationCreatedAt', // Default sort field
    sortDirection: 'desc', // Default sort direction
    filterParams: {}, // No filters by default
    from: (() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().split('T')[0]; })(), // Default to last 30 days
    to: new Date().toISOString().split('T')[0], // Default to today
    lastRequestSource: null,
    dateLabelKey: 'last30Days'
  },
  chatDetail: {
    data: [],
    loading: false,
    error: null,
    errorStatusCode: null,
    totalCount: 0,
    totalPages: 0,
    page: 1,
    pageSize: 10,
    hasNextPage: false,
    hasPreviousPage: false,
    conversationId: null,
    conversationSubject: undefined,
    lastRequestSource: null
  },
  currentChatHistory: null
};

// Async thunks
export const fetchChatHistory = createAsyncThunk<
  FetchChatHistoryPayload,
  FetchChatHistoryArgs,
  { rejectValue: SidekickErrorPayload }
>(
  'sidekick/fetchChatHistory',
  async ({
    page,
    pageSize,
    sortBy,
    sortDirection,
    from,
    to,
    filters,
    requestSource
  }: FetchChatHistoryArgs, { getState, rejectWithValue }) => {
    // Always get the current sort state
    const state = getState() as RootState;
    const currentState = state.sidekick.chatHistory;

    // Use provided parameters or fall back to current state
    const effectivePage = page || currentState.currentPage;
    const effectivePageSize = pageSize || currentState.rowsPerPage;
    const effectiveSortBy = sortBy || currentState.sortField;
    const effectiveSortDirection = sortDirection || currentState.sortDirection;

    // TODO - implement filters in the API and pass them here when available

    try {
      const response = await sidekickService.getChatHistory(
        effectivePage,
        effectivePageSize,
        effectiveSortBy,
        effectiveSortDirection,
        from,
        to,
        filters
      );

      // Use the mapping function to transform the API response to the expected format
      const mappedResponse = mapChatHistoryResponse(response.data);
      return {
        data: mappedResponse.value,
        totalCount: mappedResponse.count,
        page: response.data.page,
        pageSize: response.data.pageSize,
        sortBy: effectiveSortBy,
        sortDirection: effectiveSortDirection,
        requestSource: requestSource || null
      };
    } catch (error) {
      return rejectWithValue(getApiErrorPayload(error, 'Failed to fetch chat history'));
    }
  }
);

export const fetchChatSessionDetails = createAsyncThunk<
  FetchChatSessionDetailsPayload,
  FetchChatSessionDetailsArgs,
  { rejectValue: SidekickErrorPayload }
>(
  'sidekick/fetchChatSessionDetails',
  async ({
    conversationId,
    page = 1,
    pageSize = 10,
    requestSource
  }: FetchChatSessionDetailsArgs, { rejectWithValue }) => {
    try {
      const response = await sidekickService.getChatSessionDetails(conversationId, page, pageSize);

      // Check if the response has the expected structure
      if (response.data && response.data.data && response.data.data[0] && response.data.data[0].messages) {
        return {
          data: response.data.data[0].messages,
          totalCount: response.data.totalCount || 0,
          totalPages: response.data.totalPages || 1,
          page: response.data.page || 1,
          pageSize: response.data.pageSize || 10,
          hasNextPage: response.data.hasNextPage || false,
          hasPreviousPage: response.data.hasPreviousPage || false,
          conversationId,
          // Include the conversation subject if available in the response
          conversationSubject: response.data.data[0].chatSubject || undefined,
          requestSource: requestSource || null
        };
      } else {
        // Log error in development only
        if (process.env.NODE_ENV === 'development') {
          console.error('Unexpected API response structure:', response.data);
        }
        return {
          data: [],
          totalCount: 0,
          totalPages: 1,
          page: 1,
          pageSize: 10,
          hasNextPage: false,
          hasPreviousPage: false,
          conversationId,
          conversationSubject: 'Session Chat',
          requestSource: requestSource || null
        };
      }
    } catch (error) {
      // Log error in development only
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching chat session details:', error);
      }
      return rejectWithValue(getApiErrorPayload(error, 'Failed to fetch chat session details'));
    }
  }
);

// Export chat history data - will be used once the API is ready
export const exportChatHistory = createAsyncThunk(
  'sidekick/exportChatHistory',
  async ({
    format,
    filters
  }: {
    format: 'csv' | 'excel';
    filters?: Record<string, string>
  }) => {
    // This will be implemented when the API is ready
    const response = await sidekickService.exportChatHistory(format, filters);
    return response.data;
  }
);

// Create the slice
export const sidekickSlice = createSlice({
  name: 'sidekick',
  initialState,
  reducers: {
    /**
     * Sets the conversation subject for the chat detail view
     * @param state Current Redux state
     * @param action Action containing the conversation ID and subject
     */
    setConversationSubject: (state, action: PayloadAction<{ conversationId: string; conversationSubject: string }>) => {
      state.chatDetail.conversationId = action.payload.conversationId;
      state.chatDetail.conversationSubject = action.payload.conversationSubject;
    },

    /**
     * Clears the chat history data and resets pagination
     * @param state Current Redux state
     */
    clearChatHistory: (state) => {
      state.chatHistory.data = [];
      state.chatHistory.totalCount = 0;
      state.chatHistory.currentPage = 1; // Reset to first page
      state.chatHistory.errorStatusCode = null;
    },

    /**
     * Sets the current page for chat history pagination
     * @param state Current Redux state
     * @param action Action containing the new page number
     */
    setChatHistoryCurrentPage: (state, action: PayloadAction<number>) => {
      state.chatHistory.currentPage = action.payload;
    },

    /**
     * Sets the number of rows per page for chat history pagination
     * Also resets to the first page when changing rows per page
     * @param state Current Redux state
     * @param action Action containing the new rows per page value
     */
    setChatHistoryRowsPerPage: (state, action: PayloadAction<number>) => {
      state.chatHistory.rowsPerPage = action.payload;
      // Reset to first page when changing rows per page
      state.chatHistory.currentPage = 1;
    },

    /**
     * Sets the sort field for chat history
     * @param state Current Redux state
     * @param action Action containing the new sort field
     */
    setChatHistorySortField: (state, action: PayloadAction<string>) => {
      state.chatHistory.sortField = action.payload;
    },

    /**
     * Sets the sort direction for chat history
     * @param state Current Redux state
     * @param action Action containing the new sort direction
     */
    setChatHistorySortDirection: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.chatHistory.sortDirection = action.payload;
    },
    /**
     * Sets the filter parameters for chat history
     * @param state Current Redux state
     * @param action Action containing the new filter params
     */
    setChatHistoryFilterParams: (state, action: PayloadAction<Record<string, string | number | string[]>>) => {
      state.chatHistory.filterParams = action.payload;
      state.chatHistory.currentPage = 1; // Optionally reset to first page
    },

    /**
     * Sets the predefined date label key for the date picker display
     * @param state Current Redux state
     * @param action Action containing the preset key or null for custom range
     */
    setChatHistoryDateLabelKey: (state, action: PayloadAction<string | null>) => {
      state.chatHistory.dateLabelKey = action.payload;
    },

    clearChatDetail: (state) => {
      state.chatDetail.data = [];
      state.chatDetail.totalCount = 0;
      state.chatDetail.totalPages = 0;
      state.chatDetail.page = 1;
      state.chatDetail.pageSize = 10;
      state.chatDetail.hasNextPage = false;
      state.chatDetail.hasPreviousPage = false;
      state.chatDetail.conversationId = null;
      state.chatDetail.conversationSubject = undefined;
      state.chatDetail.lastRequestSource = null;
      state.chatDetail.errorStatusCode = null;
    },
    clearCurrentChatHistory: (state) => {
      state.currentChatHistory = null;
    }
  },
  extraReducers: (builder) => {
    // Handle fetchChatHistory
    builder
      .addCase(fetchChatHistory.pending, (state, action) => {
        state.chatHistory.loading = true;
        state.chatHistory.error = null;
        state.chatHistory.errorStatusCode = null;
        state.chatHistory.lastRequestSource = (action.meta.arg?.requestSource as 'initial' | 'filter' | 'pagination' | 'sorting' | undefined) || null;
      })
      .addCase(fetchChatHistory.fulfilled, (state, action) => {
        state.chatHistory.loading = false;
        state.chatHistory.data = action.payload.data;
        state.chatHistory.totalCount = action.payload.totalCount;
        state.chatHistory.lastRequestSource = action.payload.requestSource;

        // Update pagination state based on API response
        if (action.payload.page) {
          state.chatHistory.currentPage = action.payload.page;
        }

        // Update rows per page if provided
        if (action.payload.pageSize) {
          state.chatHistory.rowsPerPage = action.payload.pageSize;
        }

        if (action.payload.sortBy) {
          state.chatHistory.sortField = action.payload.sortBy;
        }

        if (action.payload.sortDirection) {
          state.chatHistory.sortDirection = action.payload.sortDirection;
        }

        state.chatHistory.errorStatusCode = null;
      })
      .addCase(fetchChatHistory.rejected, (state, action) => {
        state.chatHistory.loading = false;
        state.chatHistory.error = action.payload?.message || action.error.message || 'Failed to fetch chat history';
        state.chatHistory.errorStatusCode = action.payload?.statusCode ?? null;
        state.chatHistory.lastRequestSource = (action.meta.arg?.requestSource as 'initial' | 'filter' | 'pagination' | 'sorting' | undefined) || null;
      })

      // Handle fetchChatSessionDetails
      .addCase(fetchChatSessionDetails.pending, (state, action) => {
        state.chatDetail.loading = true;
        state.chatDetail.error = null;
        state.chatDetail.errorStatusCode = null;
        state.chatDetail.lastRequestSource = (action.meta.arg?.requestSource as 'initial' | 'pagination' | undefined) || null;
      })
      .addCase(fetchChatSessionDetails.fulfilled, (state, action) => {
        state.chatDetail.loading = false;
        state.chatDetail.data = action.payload.data;
        state.chatDetail.totalCount = action.payload.totalCount;
        state.chatDetail.totalPages = action.payload.totalPages;
        state.chatDetail.page = action.payload.page;
        state.chatDetail.pageSize = action.payload.pageSize;
        state.chatDetail.hasNextPage = action.payload.hasNextPage;
        state.chatDetail.hasPreviousPage = action.payload.hasPreviousPage;
        state.chatDetail.conversationId = action.payload.conversationId;
        state.chatDetail.lastRequestSource = action.payload.requestSource;
        // Update the conversation subject if provided in the payload
        if (action.payload.conversationSubject) {
          state.chatDetail.conversationSubject = action.payload.conversationSubject;
        }

        state.chatDetail.errorStatusCode = null;
      })
      .addCase(fetchChatSessionDetails.rejected, (state, action) => {
        state.chatDetail.loading = false;
        state.chatDetail.error = action.payload?.message || action.error.message || 'Failed to fetch chat session details';
        state.chatDetail.errorStatusCode = action.payload?.statusCode ?? null;
        state.chatDetail.lastRequestSource = (action.meta.arg?.requestSource as 'initial' | 'pagination' | undefined) || null;
      })

      // Handle exportChatHistory - will be implemented when API is ready
      .addCase(exportChatHistory.pending, (state) => {
        // Could add an export loading state here if needed
      })
      .addCase(exportChatHistory.fulfilled, (state) => {
        // Handle successful export - could show a notification or update UI
      })
      .addCase(exportChatHistory.rejected, (state, action) => {
        // Handle export error - could show an error notification
      });
  }
});

// Export actions
export const {
  setConversationSubject,
  clearChatHistory,
  clearChatDetail,
  clearCurrentChatHistory,
  setChatHistoryCurrentPage,
  setChatHistoryRowsPerPage,
  setChatHistorySortField,
  setChatHistorySortDirection,
  setChatHistoryFilterParams,
  setChatHistoryDateLabelKey
} = sidekickSlice.actions;

// Export selectors
/**
 * Selector for chat history state
 * Includes pagination state (currentPage, rowsPerPage)
 */
export const selectChatHistory = (state: RootState) => state.sidekick.chatHistory;

/**
 * Selector for chat detail state
 */
export const selectChatDetail = (state: RootState) => state.sidekick.chatDetail;

/**
 * Selector for current chat history item
 */
export const selectCurrentChatHistory = (state: RootState) => state.sidekick.currentChatHistory;

// Export reducer
export default sidekickSlice.reducer;